import React, { useState } from 'react';

import type { ReactNode } from '@tanstack/react-router';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { HiOutlineDotsVertical } from 'react-icons/hi';
import { HiOutlineDocumentCurrencyEuro } from 'react-icons/hi2';
import { RxBookmark, RxHeart } from 'react-icons/rx';

import { Favorite } from './Actions/Favorite';
import { HouseHoldCalculations } from './Actions/HouseHoldCalculations';
import { Info } from './Actions/info';
import { MoreActionsMenu } from './Actions/MoreActionsMenu';
import { Teaser } from './Actions/Teaser';
import { EmailMessageDialog } from '~/features/emailMessageDialog';
import type { OfferEntry } from '~/features/leadDetails/components/BankCreditRequest/OfferHistory/schemas/offers.schema';
import { LEAD_DETAILS_OVERLAYS } from '~/features/leadDetails/constants/overlays';
import { useLeadDetailsModalNavigation } from '~/features/leadDetails/hooks/useLeadDetailsModalNavigation';

type Props = {
    offer: SchemaLoanRequestResolver_GetAcceptedOffers[number] | OfferEntry;
};

type AcceptedOffer = SchemaLoanRequestResolver_GetAcceptedOffers[number];

// Type guard function
const isAcceptedOffer = (offer: Props['offer']): offer is AcceptedOffer => {
    return 'providerApplicationNumber' in offer;
};

const DisabledIconWrapper = ({ children }: { children: ReactNode }) => (
    <div className="flex items-center gap-2 py-1.5 px-3">
        <button
            type="button"
            disabled
            className="flex items-center gap-2 w-full cursor-not-allowed disabled:opacity-50"
        >
            {children}
        </button>
    </div>
);

const ActionButtons = ({
    offer,
    onOpenConsorsNotes,
    onOpenOfferEmailDialog,
}: {
    offer: Props['offer'];
    onOpenConsorsNotes: () => void;
    onOpenOfferEmailDialog: (offerId: string) => void;
}) => {
    if (isAcceptedOffer(offer)) {
        return (
            <>
                <HouseHoldCalculations
                    offerId={offer.id}
                    offerSource={offer.source}
                    aggregateId={offer.loanRequestId}
                    routeControlled
                />
                <Favorite acceptedOffer={offer} />
                <Teaser acceptedOffer={offer} />
                <MoreActionsMenu
                    acceptedOffer={offer}
                    onOpenConsorsNotes={onOpenConsorsNotes}
                    onOpenOfferEmailDialog={onOpenOfferEmailDialog}
                />
            </>
        );
    }

    return (
        <>
            <DisabledIconWrapper>
                <HiOutlineDocumentCurrencyEuro size={16} />
            </DisabledIconWrapper>
            <DisabledIconWrapper>
                <RxHeart size={16} />
            </DisabledIconWrapper>
            <DisabledIconWrapper>
                <RxBookmark size={16} />
            </DisabledIconWrapper>
            <DisabledIconWrapper>
                <HiOutlineDotsVertical size={16} />
            </DisabledIconWrapper>
        </>
    );
};

export const ActionBar: React.FC<Props> = ({ offer }) => {
    const [offerEmailDialogOfferId, setOfferEmailDialogOfferId] = useState<string | null>(null);
    const loanRequestId = 'loanRequestId' in offer ? offer.loanRequestId : null;
    const { openModal } = useLeadDetailsModalNavigation();

    return (
        <>
            {/* Dialogs and menus below are portalled, but React still bubbles their clicks to the row's onClick. */}
            <td className="hidden" onClick={(event) => event.stopPropagation()}>
                {offerEmailDialogOfferId && loanRequestId && (
                    <EmailMessageDialog
                        initialOfferId={offerEmailDialogOfferId}
                        leadId={loanRequestId}
                        onClose={() => setOfferEmailDialogOfferId(null)}
                    />
                )}
            </td>
            <td
                className="text-14 text-midnight whitespace-nowrap px-4 py-3"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="flex items-center justify-end gap-1">
                    <Info offer={offer} routeControlled />
                    <ActionButtons
                        offer={offer}
                        onOpenConsorsNotes={() => openModal(LEAD_DETAILS_OVERLAYS.CONSORS_NOTES)}
                        onOpenOfferEmailDialog={(offerId) => setOfferEmailDialogOfferId(offerId)}
                    />
                </div>
            </td>
        </>
    );
};
